import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Select, Spin } from 'antd';
import { useInvMoveCatForm } from './hooks/useInvMoveCatForm';

const M101Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, name, setName, sign, setSign, ratio, setRatio,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doCopy, doDelete, quickSearch,
  } = useInvMoveCatForm();

  const [searchText, setSearchText] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyCode, setCopyCode] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.fm010_01} ${current.fm010_11}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleCopy = () => {
    if (!current) return;
    setCopyCode('');
    setCopyModalOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyCode.trim()) return;
    await doCopy(copyCode.trim());
    setCopyModalOpen(false);
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          庫存異動類別設定 (pwm101f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">類別代號 (FM010_01)</label>
            <Input
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={50}
              disabled={mode !== 'add'}
              style={{ width: 300 }}
              placeholder="新增時輸入代號"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">名稱 (FM010_11)</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              disabled={!isEditing}
              style={{ width: 400 }}
            />
          </div>
          <div className="flex gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">庫存 +/- (FM010_12)</label>
              <Select
                value={sign}
                onChange={v => setSign(v)}
                disabled={!isEditing}
                style={{ width: 120 }}
                options={[
                  { value: '+', label: '+ 增加' },
                  { value: '-', label: '- 減少' },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">允許異動比率 (FM010_21)</label>
              <InputNumber
                value={ratio}
                onChange={v => setRatio(v ?? 0)}
                min={0}
                max={100}
                step={0.01}
                disabled={!isEditing}
                style={{ width: 150 }}
                addonAfter="%"
              />
            </div>
          </div>
        </div>

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.fm010_91} | 建立日期: {current.fm010_92 || '-'}</div>
            <div>修改人員: {current.fm010_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button onClick={handleCopy} disabled={!current}>複製</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>

        {/* Copy Modal */}
        <Modal
          title="複製記錄"
          open={copyModalOpen}
          onOk={handleCopyConfirm}
          onCancel={() => setCopyModalOpen(false)}
          okText="確定"
          cancelText="取消"
        >
          <p className="mb-2">來源: {current?.fm010_01} {current?.fm010_11}</p>
          <Input
            placeholder="請輸入新代號"
            value={copyCode}
            onChange={e => setCopyCode(e.target.value)}
            maxLength={50}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default M101Form;
